import { UserType } from '@metadevprofile/shared';
import { buttonVariants } from '@metadevprofile/ui/web';
import {
  BellIcon,
  BotIcon,
  BrainIcon,
  Building2Icon,
  ClipboardListIcon,
  FolderIcon,
  LayoutDashboardIcon,
  NewspaperIcon,
  SmartphoneIcon,
  UserIcon,
  UsersIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, NavLink, Outlet } from 'react-router';
import { adminRoutes } from '@/admin-user/constants/routes';
import { selectUser } from '@/auth/stores/auth-slice';
import { Footer } from '@/common/components/sidebar/footer';
import { MainContentArea } from '@/common/components/sidebar/main-content-area';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarProvider,
} from '@/common/components/sidebar/sidebar';
import { ToggleSidebar } from '@/common/components/sidebar/toggle-sidebar';
import { getRouteByUserType } from '@/common/lib/get-route-by-user-type';
import { navLinkClass } from '@/common/lib/nav-link-class';
import { useAppSelector } from '@/common/redux/store';

export function AdminLayout() {
  const { t } = useTranslation('admin-user');
  const user = useAppSelector(selectUser);

  if (!user) return null;

  if (user.type !== UserType.ADMIN) {
    return <Navigate to={getRouteByUserType(user)} replace />;
  }

  const sidebarItems = {
    general: [
      {
        label: t('navigation.dashboard'),
        icon: LayoutDashboardIcon,
        path: adminRoutes.dashboard,
      },
      {
        label: t('navigation.evaluations'),
        icon: ClipboardListIcon,
        path: adminRoutes.evaluations,
      },
      {
        label: t('navigation.medias'),
        icon: FolderIcon,
        path: adminRoutes.medias,
      },
      {
        label: t('navigation.agents'),
        icon: BotIcon,
        path: adminRoutes.agents,
      },
      {
        label: t('navigation.users'),
        icon: UserIcon,
        path: adminRoutes.users,
      },
      {
        label: t('navigation.institutions'),
        icon: Building2Icon,
        path: adminRoutes.institutions,
      },
      {
        label: t('navigation.subscriptions'),
        icon: UsersIcon,
        path: adminRoutes.subscriptions,
      },
      {
        label: t('navigation.mobileApp'),
        icon: SmartphoneIcon,
        path: adminRoutes.mobileApp,
      },
      {
        label: t('navigation.changelog'),
        icon: NewspaperIcon,
        path: adminRoutes.changelog,
      },
    ],
    external: [
      {
        label: t('navigation.sensoquizz'),
        icon: BrainIcon,
        path: adminRoutes.sensoquizz,
      },
    ],
  };

  return (
    <SidebarProvider>
      <ToggleSidebar />
      <Sidebar className="bg-surface text-foreground p-2">
        <SidebarHeader>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <img
                src="/images/logo.png"
                alt={t('navigation.logoAlt')}
                style={{ width: 32, height: 32 }}
                className="size-8 rounded-md"
              />
              <div className="text font-semibold">{t('navigation.brandName')}</div>
            </div>
            <Link to={adminRoutes.notifications} className={buttonVariants({ variant: 'ghost', isIconOnly: true })}>
              <BellIcon className="size-4" />
            </Link>
          </div>
        </SidebarHeader>
        <SidebarContent className="flex-1 mt-2">
          <SidebarGroup>
            <SidebarGroupLabel className="text-muted text-xs font-medium uppercase tracking-wider">
              {t('navigation.general')}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {sidebarItems.general.map((item) => (
                  <SidebarMenuItem key={item.path}>
                    <NavLink to={item.path} end className={({ isActive }) => navLinkClass(isActive)}>
                      <item.icon className="size-4" />
                      {item.label}
                    </NavLink>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
            <SidebarGroupLabel className="text-muted text-xs font-medium uppercase tracking-wider">
              {t('navigation.external')}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {sidebarItems.external.map((item) => (
                  <SidebarMenuItem key={item.path}>
                    <NavLink to={item.path} end className={({ isActive }) => navLinkClass(isActive)}>
                      <item.icon className="size-4" />
                      {item.label}
                    </NavLink>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <Footer />
        </SidebarFooter>
      </Sidebar>
      <MainContentArea>
        <Outlet />
      </MainContentArea>
    </SidebarProvider>
  );
}
